import { createSelector } from 'reselect'

import { createDeepEqualSelector } from './createDeepEqualSelector'
import { allFamilyTreesSelector } from './familyTreeFirstOrder'
import { currentFamilyTreeIdSelector } from './secondOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../types/plottr').FamilyTree} FamilyTree
 * @typedef {import('../types/plottr').Member} Member
 * @typedef {import('../types/plottr').Translation} Translation
 * @typedef {import('../types/plottr').RelationshipConfiguration} RelationshipConfiguration
 */

/**
 * @typedef {Record<String, Member>} Members
 */

/**
 * @type {function(PlottrState): (RelationshipConfiguration | Null)}
 */
const _currentFamilyTreeRelationshipConfigurationSelector = createSelector(
  allFamilyTreesSelector,
  currentFamilyTreeIdSelector,
  (allFamilyTrees, currentFamilyTreeId) => {
    if (typeof currentFamilyTreeId === 'number' && allFamilyTrees[currentFamilyTreeId]) {
      return allFamilyTrees[currentFamilyTreeId]?.relationshipConfiguration || null
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): (RelationshipConfiguration | Null)}
 */
export const currentFamilyTreeRelationshipConfigurationSelector = createDeepEqualSelector(
  _currentFamilyTreeRelationshipConfigurationSelector,
  (currentRelationshipConfiguration) => {
    return currentRelationshipConfiguration
  }
)

/**
 * @type {function(PlottrState): (Members | Null)}
 */
const _currentFamilyTreeMembersSelector = createSelector(
  allFamilyTreesSelector,
  currentFamilyTreeIdSelector,
  (allFamilyTrees, currentFamilyTreeId) => {
    if (typeof currentFamilyTreeId === 'number' && allFamilyTrees[currentFamilyTreeId]) {
      return allFamilyTrees[currentFamilyTreeId].members
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): (Members | Null)}
 */
export const currentFamilyTreeMembersSelector = createDeepEqualSelector(
  _currentFamilyTreeMembersSelector,
  (members) => {
    return members
  }
)

const _currentFamilyTreeMetadataSelector = createSelector(
  allFamilyTreesSelector,
  currentFamilyTreeIdSelector,
  (allFamilyTrees, currentFamilyTreeId) => {
    if (typeof currentFamilyTreeId === 'number' && allFamilyTrees[currentFamilyTreeId]) {
      const currentFamilyTree = allFamilyTrees[currentFamilyTreeId]
      return {
        name: currentFamilyTree.name,
        backgroundImageId: currentFamilyTree.backgroundImageId,
        backgroundColor: currentFamilyTree.backgroundColor,
        backgroundImageTranslation: currentFamilyTree.backgroundImageTranslation,
      }
    } else {
      return null
    }
  }
)

/**
 * @typedef FamilyTreeMetadata
 * @property {String} name
 * @property {String | Null} backgroundImageId
 * @property {String | Null} backgroundColor
 * @property {Translation | Null} backgroundImageTranslation
 */

/**
 * @type {function(PlottrState): (FamilyTreeMetadata | Null)}
 */
export const currentFamilyTreeMetadataSelector = createDeepEqualSelector(
  _currentFamilyTreeMetadataSelector,
  (metadata) => {
    return metadata
  }
)

/**
 * @type {function(PlottrState): String}
 */
export const currentFamilyTreeNameSelector = createSelector(
  currentFamilyTreeMetadataSelector,
  (metadata) => {
    return metadata?.name ?? ''
  }
)

/**
 * @type {function(PlottrState): (String | Null)}
 */
export const currentFamilyTreeBackgroundSelector = createSelector(
  currentFamilyTreeMetadataSelector,
  (metadata) => {
    return metadata?.backgroundImageId ?? null
  }
)

/**
 * @type {function(PlottrState): (Translation | Null)}
 */
export const currentFamilyTreeBackgroundTranslationSelector = createSelector(
  currentFamilyTreeMetadataSelector,
  (metadata) => {
    return metadata?.backgroundImageTranslation ?? null
  }
)

/**
 * @type {function(PlottrState): (String | Null)}
 */
export const currentFamilyTreeBackgroundColorSelector = createSelector(
  currentFamilyTreeMetadataSelector,
  (metadata) => {
    return metadata?.backgroundColor ?? null
  }
)
